/**
 * Type-safe utilities for event filter URL parameters
 */

import {
	type ClassificationFilters,
	getParam as getParamValue,
	normalizeClassificationFilters,
	parseClassificationParams,
	parseDateToObject,
	parseInt10,
} from "./filter-utils";

export type { ClassificationFilters };

export interface EventFilters {
	dateRange: [Date, Date] | null;
	engagementRange: [number, number];
	classificationFilters: ClassificationFilters;
	searchQuery: string;
	platforms: string[];
	page: number;
	sortBy: "date" | "score";
	sortOrder: "asc" | "desc";
	reviewStatus: "all" | "user" | "system" | "llm";
	/** Filter by semantic_type — array, matches any. Powers the All/Notifications/Memory bucket selector. */
	semanticTypes: string[];
	eventCategory: string | null;
	resourceKind: string | null;
	resourceId: string | null;
	/** Schedule drill context; not sent to the content API. */
	schedule: string | null;
	runId: number | null;
	contentIds: number[] | null;
	connectionIds: number[] | null;
	feedIds: number[] | null;
	runIds: number[] | null;
	/** Scope to memory written by this agent (`metadata.agent_id`). Memory sidebar rail. */
	agentId: string | null;
	/** Scope to events produced by any registration of an OAuth client. */
	clientIds: string[] | null;
	/** Internal Connected App drill-down; exact within clientIds and not URL-backed here. */
	mcpActivityId: string | null;
	/** Scope to events analyzed by any run of this automation. Memory automation drill-down. */
	automationId: number | null;
	/**
	 * Scope to events this Automation WROTE. Not the same axis as `automationId`,
	 * which is what it READ — for one prod Automation those are 354 events and
	 * 2,289 events. The Automation page shows this one; the Memory drill-down
	 * shows the other.
	 */
	producedByAutomationId: number | null;
	beforeOccurredAt: string | null;
	beforeId: number | null;
	afterOccurredAt: string | null;
	afterId: number | null;
	interactionStatus: string | null;
	/**
	 * Scope events to entities of these types. Not URL-backed: set only via the
	 * EventsTab `scopedEntityTypes` prop when browsing a single type in Memory
	 * (`browse_type`). Sent to the API as `entity_types`.
	 */
	entityTypes: string[];
}

export type PartialEventFilters = Partial<EventFilters>;

export function parseEventFiltersFromUrl(
	searchParams: URLSearchParams | Record<string, string | undefined>,
): PartialEventFilters {
	const filters: PartialEventFilters = {};
	const get = (key: string) => getParamValue(searchParams, key);

	for (const [key, field] of [
		["event_category", "eventCategory"],
		["resource_kind", "resourceKind"],
		["resource_id", "resourceId"],
	] as const) {
		const value = get(key);
		if (value) filters[field] = value;
	}

	// Platforms
	const platformsStr = get("platforms") || get("platform");
	if (platformsStr) {
		const platforms = platformsStr
			.split(",")
			.map((p) => p.trim())
			.filter((p) => p.length > 0);
		if (platforms.length > 0) {
			filters.platforms = platforms;
		}
	}

	// Date range
	const dateStart = get("date_start");
	const dateEnd = get("date_end");
	if (dateStart && dateEnd) {
		const startDate = parseDateToObject(dateStart);
		const endDate = parseDateToObject(dateEnd);
		if (startDate && endDate) {
			filters.dateRange = [startDate, endDate];
		}
	}

	// Engagement range
	const engagementMin = get("engagement_min");
	const engagementMax = get("engagement_max");
	if (engagementMin || engagementMax) {
		const min = parseInt10(engagementMin) ?? 0;
		const max = parseInt10(engagementMax) ?? 100;
		if (min !== 0 || max !== 100) {
			filters.engagementRange = [min, max];
		}
	}

	// Classification filters — JSON format first, then clf_* params
	const classificationFilters: ClassificationFilters = {};
	const classificationsStr = get("classifications");
	if (classificationsStr) {
		try {
			const parsed = JSON.parse(classificationsStr);
			Object.assign(
				classificationFilters,
				normalizeClassificationFilters(parsed),
			);
		} catch (err) {
			console.error("Failed to parse classifications from URL:", err);
		}
	}
	Object.assign(classificationFilters, parseClassificationParams(searchParams));

	if (Object.keys(classificationFilters).length > 0) {
		filters.classificationFilters = classificationFilters;
	}

	// Search query
	const searchQuery = get("q");
	if (searchQuery && searchQuery.trim().length >= 3) {
		filters.searchQuery = searchQuery.trim();
	}

	// Page
	const pageStr = get("page");
	if (pageStr) {
		const page = parseInt10(pageStr);
		if (page != null && page > 0) {
			filters.page = page;
		}
	}

	// Sort options
	const sortBy = get("sort_by");
	if (sortBy === "date" || sortBy === "score") {
		filters.sortBy = sortBy;
	}

	const sortOrder = get("sort_order");
	if (sortOrder === "asc" || sortOrder === "desc") {
		filters.sortOrder = sortOrder;
	}

	// Semantic types (bucket selector: notifications, memory, …)
	const semanticTypesStr = get("semantic_types") || get("semantic_type");
	if (semanticTypesStr) {
		const types = semanticTypesStr
			.split(",")
			.map((t) => t.trim())
			.filter((t) => t.length > 0);
		if (types.length > 0) {
			filters.semanticTypes = types;
		}
	}

	// Review status
	const reviewStatus = get("review_status");
	if (
		reviewStatus === "all" ||
		reviewStatus === "user" ||
		reviewStatus === "system" ||
		reviewStatus === "llm"
	) {
		filters.reviewStatus = reviewStatus;
	}

	const runIdStr = get("run_id");
	if (runIdStr) {
		const runId = parseInt10(runIdStr);
		if (runId != null) filters.runId = runId;
	}

	// Content IDs (from knowledge item permalink)
	const contentIdsStr = get("content_ids");
	if (contentIdsStr) {
		const ids = contentIdsStr
			.split(",")
			.map((s) => parseInt10(s.trim()))
			.filter((n): n is number => n != null);
		if (ids.length > 0) filters.contentIds = ids;
	}

	// Connection / feed / run drill-down ids (from feed-detail "View in memory")
	for (const [param, key] of [
		["connection_ids", "connectionIds"],
		["feed_ids", "feedIds"],
		["run_ids", "runIds"],
	] as const) {
		const raw = get(param);
		if (!raw) continue;
		const ids = raw
			.split(",")
			.map((s) => parseInt10(s.trim()))
			.filter((n): n is number => n != null);
		if (ids.length > 0) filters[key] = ids;
	}

	// Cursor param: "at=<timestamp>,<id>" (scroll position for infinite scroll)
	const atParam = get("at");
	if (atParam) {
		const lastComma = atParam.lastIndexOf(",");
		if (lastComma > 0) {
			const timestamp = atParam.slice(0, lastComma);
			const id = parseInt10(atParam.slice(lastComma + 1));
			if (timestamp && id != null) {
				filters.beforeOccurredAt = timestamp;
				filters.beforeId = id;
			}
		}
	}

	const interactionStatus = get("interaction_status");
	if (interactionStatus) {
		filters.interactionStatus = interactionStatus;
	}

	const agentId = get("agent") || get("agent_id");
	if (agentId?.trim()) {
		filters.agentId = agentId.trim();
	}

	const clientIdsStr = get("client_ids");
	if (clientIdsStr?.trim()) {
		const ids = clientIdsStr
			.split(",")
			.map((id) => id.trim())
			.filter(Boolean);
		if (ids.length > 0) filters.clientIds = ids;
	}

	// MCP conversation/session drill. The backend only honors it alongside
	// client_ids, so ignore an orphaned value instead of issuing a 400 query.
	const mcpActivityId = get("mcp_activity_id");
	if (mcpActivityId?.trim() && filters.clientIds) {
		filters.mcpActivityId = mcpActivityId.trim();
	}

	const schedule = get("schedule")?.trim();
	if (schedule) filters.schedule = schedule;

	const automationStr = get("automation");
	if (automationStr) {
		const automationId = parseInt10(automationStr);
		if (automationId != null) filters.automationId = automationId;
	}

	// Separate key from `automation` on purpose: they are opposite directions
	// (wrote vs read) and a URL has to be able to say which one it meant.
	const producedByStr = get("produced_by");
	if (producedByStr) {
		const producedByAutomationId = parseInt10(producedByStr);
		if (producedByAutomationId != null) {
			filters.producedByAutomationId = producedByAutomationId;
		}
	}

	return filters;
}

export function serializeEventFiltersToSearch(
	filters: PartialEventFilters,
): Record<string, string | undefined> {
	const params: Record<string, string | undefined> = {};

	if (filters.platforms && filters.platforms.length > 0) {
		params.platforms = filters.platforms.join(",");
	}

	if (filters.dateRange) {
		params.date_start = filters.dateRange[0].toISOString().split("T")[0];
		params.date_end = filters.dateRange[1].toISOString().split("T")[0];
	}

	if (filters.engagementRange) {
		if (filters.engagementRange[0] !== 0) {
			params.engagement_min = filters.engagementRange[0].toString();
		}
		if (filters.engagementRange[1] !== 100) {
			params.engagement_max = filters.engagementRange[1].toString();
		}
	}

	if (
		filters.classificationFilters &&
		Object.keys(filters.classificationFilters).length > 0
	) {
		const normalized = normalizeClassificationFilters(
			filters.classificationFilters,
		);
		if (Object.keys(normalized).length > 0) {
			params.classifications = JSON.stringify(normalized);
		}
	}

	if (filters.searchQuery && filters.searchQuery.trim().length >= 3) {
		params.q = filters.searchQuery.trim();
	}

	if (filters.sortBy) {
		params.sort_by = filters.sortBy;
	}

	if (filters.sortOrder && filters.sortOrder !== "desc") {
		params.sort_order = filters.sortOrder;
	}

	if (filters.reviewStatus && filters.reviewStatus !== "all") {
		params.review_status = filters.reviewStatus;
	}

	if (filters.eventCategory) params.event_category = filters.eventCategory;
	if (filters.resourceKind) params.resource_kind = filters.resourceKind;
	if (filters.resourceId) params.resource_id = filters.resourceId;

	if (filters.semanticTypes && filters.semanticTypes.length > 0) {
		params.semantic_types = filters.semanticTypes.join(",");
	}

	if (filters.runId != null) {
		params.run_id = filters.runId.toString();
	}

	if (filters.contentIds && filters.contentIds.length > 0) {
		params.content_ids = filters.contentIds.join(",");
	}

	if (filters.connectionIds && filters.connectionIds.length > 0) {
		params.connection_ids = filters.connectionIds.join(",");
	}

	if (filters.feedIds && filters.feedIds.length > 0) {
		params.feed_ids = filters.feedIds.join(",");
	}

	if (filters.runIds && filters.runIds.length > 0) {
		params.run_ids = filters.runIds.join(",");
	}

	if (filters.agentId) {
		params.agent = filters.agentId;
	}

	if (filters.clientIds && filters.clientIds.length > 0) {
		params.client_ids = filters.clientIds.join(",");
	}

	if (filters.mcpActivityId) {
		params.mcp_activity_id = filters.mcpActivityId;
	}

	if (filters.schedule) params.schedule = filters.schedule;

	if (filters.automationId != null) {
		params.automation = filters.automationId.toString();
	}

	if (filters.producedByAutomationId != null) {
		params.produced_by = filters.producedByAutomationId.toString();
	}

	if (filters.beforeOccurredAt && filters.beforeId != null) {
		params.at = `${filters.beforeOccurredAt},${filters.beforeId}`;
	}

	if (filters.interactionStatus) {
		params.interaction_status = filters.interactionStatus;
	}

	return params;
}

/**
 * Every URL param {@link serializeEventFiltersToSearch} can emit. The events tab
 * passes this to useTabFilters as the set of keys to delete before writing fresh
 * filter params. Anything the serializer can produce MUST be listed here — a
 * missing key goes stale in the URL when its filter clears (e.g. clicking the
 * "All" bucket could not drop semantic_types). Co-located with the serializer and
 * guarded by a unit test so the two cannot drift.
 */
export const EVENT_FILTER_KEYS = [
	"event_category",
	"resource_kind",
	"resource_id",
	"platform",
	"semantic_type",
	"agent_id",
	"page",
	"platforms",
	"date_start",
	"date_end",
	"engagement_min",
	"engagement_max",
	"classifications",
	"q",
	"sort_by",
	"sort_order",
	"review_status",
	"semantic_types",
	"run_id",
	"content_ids",
	"connection_ids",
	"feed_ids",
	"run_ids",
	"agent",
	"client_ids",
	"mcp_activity_id",
	"automation",
	"produced_by",
	"schedule",
	"at",
	"interaction_status",
];

export function hasEventCursor(filters: PartialEventFilters): boolean {
	return (
		(filters.beforeOccurredAt != null && filters.beforeId != null) ||
		(filters.afterOccurredAt != null && filters.afterId != null)
	);
}

export function convertFiltersToApiParams(
	filters: PartialEventFilters,
	entityId?: number,
	options: {
		limit?: number;
		offset?: number;
		includeClassification?:
			| "summary"
			| "timeline"
			| "summary,timeline"
			| "timeline,summary";
		sort_by?: "date" | "score";
		sort_order?: "asc" | "desc";
	} = {},
): Record<string, unknown> {
	const limit = options.limit ?? 50;
	const page = filters.page ?? 1;
	const hasCursor = hasEventCursor(filters);
	const useDateFeed =
		(filters.sortBy ?? options.sort_by ?? "score") === "date" &&
		(filters.sortOrder ?? options.sort_order ?? "desc") === "desc";
	const params: Record<string, unknown> = {
		limit,
		offset:
			hasCursor || useDateFeed ? 0 : (options.offset ?? (page - 1) * limit),
	};
	// Only `include_classification` (singular) is a read_knowledge arg.
	// Plural `include_classifications` is internal to content-search and is
	// always enabled by the tool handler — sending it is rejected as unknown.
	// Opt-in: the per-value counts are a stats concern, not a feed-page one.
	if (options.includeClassification) {
		params.include_classification = options.includeClassification;
	}

	if (entityId !== undefined) {
		params.entity_id = entityId;
	}

	params.sort_by = filters.sortBy ?? options.sort_by ?? "score";
	params.sort_order = filters.sortOrder ?? options.sort_order ?? "desc";

	if (filters.platforms && filters.platforms.length > 0) {
		params.platforms = filters.platforms;
	}

	if (filters.dateRange) {
		params.since = filters.dateRange[0].toISOString().split("T")[0];
		params.until = filters.dateRange[1].toISOString().split("T")[0];
	}

	if (filters.engagementRange) {
		if (filters.engagementRange[0] !== 0) {
			params.engagement_min = filters.engagementRange[0];
		}
		if (filters.engagementRange[1] !== 100) {
			params.engagement_max = filters.engagementRange[1];
		}
	}

	if (filters.classificationFilters) {
		const normalized = normalizeClassificationFilters(
			filters.classificationFilters,
		);
		if (Object.keys(normalized).length > 0) {
			params.classification_filters = normalized;
		}
	}

	if (filters.searchQuery && filters.searchQuery.trim().length >= 3) {
		params.query = filters.searchQuery.trim();
	}

	if (filters.reviewStatus && filters.reviewStatus !== "all") {
		params.classification_source =
			filters.reviewStatus === "system" ? "embedding" : filters.reviewStatus;
	}

	if (filters.eventCategory) params.event_category = filters.eventCategory;
	if (filters.resourceKind) params.resource_kind = filters.resourceKind;
	if (filters.resourceId) params.resource_id = filters.resourceId;

	if (filters.semanticTypes && filters.semanticTypes.length > 0) {
		// Backend accepts string for one, array for many — keep the wire shape minimal
		// and avoid the array path through the vector-search SQL when only one type is set.
		params.semantic_type =
			filters.semanticTypes.length === 1
				? filters.semanticTypes[0]
				: filters.semanticTypes;
	}

	if (filters.runId != null) {
		params.run_id = filters.runId;
	}

	if (filters.contentIds && filters.contentIds.length > 0) {
		params.content_ids = filters.contentIds;
	}

	if (filters.connectionIds && filters.connectionIds.length > 0) {
		params.connection_ids = filters.connectionIds;
	}

	if (filters.feedIds && filters.feedIds.length > 0) {
		params.feed_ids = filters.feedIds;
	}

	if (filters.runIds && filters.runIds.length > 0) {
		params.run_ids = filters.runIds;
	}

	if (filters.producedByAutomationId != null) {
		params.produced_by_automation_id = filters.producedByAutomationId;
	}

	if (filters.automationId != null) {
		params.analyzed_by_automation_id = filters.automationId;
	}
	if (filters.agentId) {
		params.agent_id = filters.agentId;
	}

	if (filters.clientIds && filters.clientIds.length > 0) {
		params.client_ids = filters.clientIds;
	}
	if (filters.mcpActivityId) {
		params.mcp_activity_id = filters.mcpActivityId;
	}

	if (filters.beforeOccurredAt && filters.beforeId != null) {
		params.before_occurred_at = filters.beforeOccurredAt;
		params.before_id = filters.beforeId;
	}

	if (filters.afterOccurredAt && filters.afterId != null) {
		params.after_occurred_at = filters.afterOccurredAt;
		params.after_id = filters.afterId;
	}

	if (filters.interactionStatus) {
		params.interaction_status = filters.interactionStatus;
	}

	if (filters.entityTypes && filters.entityTypes.length > 0) {
		params.entity_types = filters.entityTypes;
	}

	return params;
}

export function applyEventTabDefaults(
	filters: PartialEventFilters,
): PartialEventFilters {
	return {
		sortBy: filters.sortBy ?? "date",
		sortOrder: filters.sortOrder ?? "desc",
		...filters,
	};
}

export function isDateFeedMode(filters: PartialEventFilters): boolean {
	const resolved = applyEventTabDefaults(filters);
	return resolved.sortBy === "date" && resolved.sortOrder === "desc";
}

export function hasActiveFilters(filters: PartialEventFilters): boolean {
	return (
		!!filters.eventCategory ||
		!!filters.resourceKind ||
		!!filters.resourceId ||
		(filters.searchQuery?.trim() ?? "").length > 0 ||
		(filters.platforms?.length ?? 0) > 0 ||
		(filters.dateRange !== null && filters.dateRange !== undefined) ||
		(filters.engagementRange?.[0] ?? 0) !== 0 ||
		(filters.engagementRange?.[1] ?? 100) !== 100 ||
		(filters.reviewStatus ?? "all") !== "all" ||
		(filters.semanticTypes?.length ?? 0) > 0 ||
		Object.keys(filters.classificationFilters ?? {}).length > 0 ||
		filters.runId != null ||
		(filters.contentIds != null && filters.contentIds.length > 0) ||
		(filters.connectionIds != null && filters.connectionIds.length > 0) ||
		(filters.feedIds != null && filters.feedIds.length > 0) ||
		(filters.runIds != null && filters.runIds.length > 0) ||
		(filters.agentId != null && filters.agentId !== "") ||
		(filters.clientIds != null && filters.clientIds.length > 0) ||
		(filters.mcpActivityId != null && filters.mcpActivityId !== "") ||
		Boolean(filters.schedule) ||
		filters.automationId != null ||
		filters.producedByAutomationId != null ||
		(filters.interactionStatus != null && filters.interactionStatus !== "")
	);
}
